import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, CalendarDays } from 'lucide-react';
import Footer from '../components/Footer';
import PublicNav from '../components/PublicNav';
import { Button } from '../components/ui';
import { blogService, BlogPost } from '../services/blogService';
import { getUploadUrl } from '../utils/url';

const formatDate = (value?: string | null) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const setMetaTag = (name: string, content: string, property = false) => {
  const attr = property ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
};

const setCanonical = (href: string) => {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
};

const JSONLD_ID = 'blog-post-jsonld';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let active = true;
    setLoading(true);
    setNotFound(false);
    blogService
      .getBySlug(slug)
      .then((data) => {
        if (active) setPost(data);
      })
      .catch((error) => {
        console.error('Error loading blog post:', error);
        if (active) setNotFound(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  // SEO meta + structured data
  useEffect(() => {
    if (!post) return;

    const title = `${post.title} — Solofix blog`;
    const description =
      post.metaDescription ||
      post.excerpt ||
      `${post.title} — lees het op de Solofix blog.`;
    const url = window.location.href;
    const image = getUploadUrl(post.coverImage);

    document.title = title;
    setMetaTag('description', description);
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', 'article', true);
    setMetaTag('og:url', url, true);
    setMetaTag('twitter:card', image ? 'summary_large_image' : 'summary');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    if (image) {
      setMetaTag('og:image', image, true);
      setMetaTag('twitter:image', image);
    }
    setCanonical(url);

    // JSON-LD Article structured data
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description,
      ...(image ? { image } : {}),
      datePublished: post.publishedAt || post.createdAt,
      dateModified: post.updatedAt,
      author: {
        '@type': post.author ? 'Person' : 'Organization',
        name: post.author?.username || 'Solofix',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Solofix',
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    };

    let script = document.getElementById(JSONLD_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = JSONLD_ID;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      document.title = 'Solofix';
      const existing = document.getElementById(JSONLD_ID);
      if (existing) existing.remove();
    };
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neon-pink" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <PublicNav />
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-2xl font-bold tracking-tight text-fg-bright mb-4">
            Blogbericht niet gevonden
          </h1>
          <p className="text-fg-muted mb-6">
            Dit bericht bestaat niet of is niet meer beschikbaar.
          </p>
          <Link to="/blog">
            <Button variant="primary">Naar de blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  const cover = getUploadUrl(post.coverImage);

  return (
    <div className="min-h-screen">
      <PublicNav />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        <Link
          to="/blog"
          className="inline-flex items-center text-sm text-fg-muted hover:text-fg-bright mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Terug naar de blog
        </Link>

        <div className="sf-eyebrow mb-3">// blog</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-fg-bright leading-tight mb-4">
          {post.title}
        </h1>

        <div className="flex items-center text-sm text-fg-muted mb-8">
          <CalendarDays className="w-4 h-4 mr-2 text-neon-cyan" />
          {formatDate(post.publishedAt || post.createdAt)}
          {post.author?.username && <span className="mx-2">·</span>}
          {post.author?.username && <span>door {post.author.username}</span>}
        </div>

        {cover && (
          <div className="rounded-card overflow-hidden border border-white/[.08] shadow-panel mb-10">
            <img src={cover} alt={post.title} className="w-full object-cover" />
          </div>
        )}

        <div
          className="text-fg-body text-base sm:text-lg leading-relaxed
            [&_h1]:text-2xl [&_h1]:sm:text-3xl [&_h1]:font-bold [&_h1]:text-fg-bright [&_h1]:mt-10 [&_h1]:mb-4
            [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:font-bold [&_h2]:text-fg-bright [&_h2]:mt-10 [&_h2]:mb-3
            [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-fg-bright [&_h3]:mt-8 [&_h3]:mb-2
            [&_p]:mb-5
            [&_a]:text-neon-cyan [&_a]:underline hover:[&_a]:text-neon-cyan
            [&_strong]:text-fg-soft [&_strong]:font-semibold
            [&_em]:italic
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-5 [&_ul]:space-y-1
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-5 [&_ol]:space-y-1
            [&_blockquote]:border-l-2 [&_blockquote]:border-neon-cyan [&_blockquote]:pl-4 [&_blockquote]:text-fg-muted [&_blockquote]:italic [&_blockquote]:my-6
            [&_img]:rounded-card [&_img]:my-6 [&_img]:max-w-full
            [&_code]:bg-white/[.06] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm
            [&_hr]:border-white/[.08] [&_hr]:my-10"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>

        {/* CTA */}
        <div className="sf-card mt-14 p-8 text-center">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-fg-bright mb-3">
            Ga jij ook niet graag alleen?
          </h2>
          <p className="text-fg-muted mb-6 max-w-xl mx-auto">
            Maak gratis een account, vind je crew en beleef je volgende festival samen.
          </p>
          <Link to="/register" className="inline-block">
            <Button size="lg" className="animate-neon-pulse">
              🚀 Start nu gratis
            </Button>
          </Link>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
